import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MapPin, Phone, Mail, Linkedin, Facebook, Instagram } from "lucide-react";
import { SERVICES } from "@/lib/services";
import { usePhones } from "@/lib/contacts";

export const Footer = () => {
  const { t, i18n } = useTranslation();
  const PHONES = usePhones();

  return (
    <footer className="border-t border-white/10 bg-venka-ink" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-6 py-16 grid gap-10 md:grid-cols-4">
        <div className="md:col-span-1">
          <img src="/venka-logo.png" alt="VENKA World Express" className="h-14 w-auto" data-testid="footer-logo" />
          <p className="mt-4 text-sm text-slate-400 leading-relaxed">
            The leading travel agency for flight tickets, passports, visas and immigration consultancy in Angola and abroad — reliable support at every step.
          </p>
          <div className="mt-5 flex items-center gap-3">
            {[Linkedin, Facebook, Instagram].map((Icon, i) => (
              <a key={i} href="#" className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/5 text-slate-300 hover:bg-venka-blue/20 hover:text-white transition-colors" data-testid={`footer-social-${i}`}>
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>

        <div data-testid="footer-services">
          <p className="font-semibold text-white mb-4">Services</p>
          <ul className="space-y-2.5 text-sm text-slate-400">
            {SERVICES.map((s) => (
              <li key={s.key}>
                <Link to={s.to} className="hover:text-venka-green-light transition-colors">{s.title}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div data-testid="footer-company">
          <p className="font-semibold text-white mb-4">Company</p>
          <ul className="space-y-2.5 text-sm text-slate-400">
            <li><Link to="/about" className="hover:text-venka-green-light transition-colors">About Us</Link></li>
            <li><Link to="/blog" className="hover:text-venka-green-light transition-colors">Blog</Link></li>
            <li><Link to="/apply" className="hover:text-venka-green-light transition-colors">Apply Online</Link></li>
            <li><Link to="/account/login" className="hover:text-venka-green-light transition-colors">Client Portal</Link></li>
            <li><Link to="/portal/login" className="hover:text-venka-green-light transition-colors">Staff Login</Link></li>
            <li><Link to="/contact" className="hover:text-venka-green-light transition-colors" data-testid="footer-contact-link">Contact Us</Link></li>
            <li><Link to="/privacy/my-data" className="hover:text-venka-green-light transition-colors" data-testid="footer-privacy">Data & Privacy</Link></li>
            <li><a href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/public/guide.pdf?lang=${["pt", "fr"].includes((i18n.language || "").slice(0, 2)) ? i18n.language.slice(0, 2) : "en"}`} target="_blank" rel="noreferrer" className="hover:text-venka-green-light transition-colors" data-testid="footer-guide">{({ pt: "Guia do cliente (PDF)", fr: "Guide client (PDF)" })[(i18n.language || "").slice(0, 2)] || "Customer guide (PDF)"}</a></li>
          </ul>
        </div>

        <div data-testid="footer-contact">
          <p className="font-semibold text-white mb-4">{t("common.footer.contactLabel", "Get in touch")}</p>
          <ul className="space-y-3 text-sm text-slate-400">
            <li className="flex gap-2.5"><MapPin className="h-4 w-4 mt-0.5 text-venka-green-light shrink-0" /><span><span className="block text-slate-200 font-medium">VENKA Angola</span>Bloco 10, Prédio 12-A, apt. 302, Cidade do Sequele, Angola</span></li>
            <li className="flex gap-2.5"><MapPin className="h-4 w-4 mt-0.5 text-venka-green-light shrink-0" /><span><span className="block text-slate-200 font-medium">VENKA Europe (Tallinn)</span>Harju maakond, Tallinn, Kesklinna linnaosa, Tartu mnt 67/1-13b, 10115</span></li>
            {["angola", "intl"].map((k) => (
              <li key={k} className="flex gap-2.5"><Phone className="h-4 w-4 mt-0.5 text-venka-green-light shrink-0" />
                <span><span className="block text-slate-200 font-medium">{PHONES[k].label[(i18n.language || "en").slice(0, 2)] || PHONES[k].label.en}</span>
                  <a href={`tel:${PHONES[k].tel}`} className="hover:text-venka-green-light" data-testid={`footer-phone-${k}`}>{PHONES[k].display}</a></span></li>
            ))}
            <li className="flex gap-2.5"><Mail className="h-4 w-4 mt-0.5 text-venka-green-light shrink-0" /> sales@venkawe.com</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-xs text-slate-500">
        <span data-testid="footer-copyright">© {new Date().getFullYear()} VENKA WORLD EXPRESS · By Farped Group OÜ. All rights reserved.</span>
      </div>
    </footer>
  );
};

export default Footer;
